/**
 * 주소록 (migration 171, 2026-09-08 · 정본 A8 · A3 · E1 · D14).
 *
 * 왼쪽에서 고르고, 오른쪽에서 본다.
 *   · 좌측 목록(A8) — 중요 · 개인 · 공유 + 태그. **접기도 개수도 없다**(정본 A8 ① 정정).
 *     머리의 개수를 안 주는 이유: 묶음마다 개수가 따로 서서, 합계가 **어느 묶음의 수도 아니다**.
 *   · 발(A8 ⑤) — 「차단한 주소」. ★ 새 화면을 만들지 않고 **메일 환경설정의 수신거부목록**
 *     으로 보낸다. 그 표(`mail_address_rules`)가 이미 있고 실제로 스팸 배치를 걸고 있다 —
 *     여기에 같은 목록을 또 그리면 «한 곳에서 풀어도 다른 곳이 막는» 상태가 생긴다.
 *   · 본문 — PageHeader(A3) + 도구줄 + 표(E1).
 *
 * ★ 「중요 주소록」은 표가 아니라 **칸**(`isStarred`)이라 개인·공유를 가로지른다(scope=ALL).
 * ★ 이름이 없으면 「(이름없음)」을 적는다 — 빈 칸이 아니라 **값**이다.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ListPane, ListPaneGroup, ListPaneItem } from '../../components/shared/ListPane';
import { PageHeader } from '../../components/shared/PageHeader';
import { Button } from '../../components/shared/Button';
import { Input } from '../../components/shared/Input';
import { Select } from '../../components/shared/Select';
import { Table, type TableColumn } from '../../components/shared/Table';
import { Toolbar } from '../../components/shared/Toolbar';
import { Badge } from '../../components/shared/Badge';
import { EmptyState } from '../../components/shared/EmptyState';
import { Card } from '../../components/shared/Card';
import {
  Star, User, Users, Tag, Ban, Plus, Search, Mail, Edit3, Trash2, Contact as ContactIcon,
} from '../../icons';
import { useAuthStore } from '../../store/authStore';
import { contactsApi, type Contact, type ContactTag } from '../../api/contacts';
import { tContacts } from '../../lib/contacts-i18n';
import { ContactEditModal } from './ContactEditModal';
import { ContactPhoto } from './ContactPhoto';
import './ContactsPage.css';

/** 좌측에서 고르는 것 — 라우트 질의로 남긴다(새로고침해도 그 자리). */
type ListKey = 'starred' | 'personal' | 'shared';

export function ContactsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const role = useAuthStore((s) => s.user?.role ?? 'STAFF');
  const canShare = role === 'ADMIN' || role === 'SYSTEM_ADMIN';
  /**
   * ★ 공유 주소록에 **담기**는 모두 (2026-09-15 · 사용자 결정 · 서버 #1374) — 고치기 · 지우기는 여전히
   *   관리자(`canShare`)다. 둘을 한 값으로 두면 「담기」를 열 때 「고치기」까지 열린다.
   */
  const canSaveShared = true;

  const list = (params.get('list') as ListKey | null) ?? 'personal';
  const tagId = params.get('tag');

  const [rows, setRows] = useState<Contact[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<{ starred: number; personal: number; shared: number }>({
    starred: 0, personal: 0, shared: 0,
  });
  const [tags, setTags] = useState<ContactTag[]>([]);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<'name' | 'email' | 'recent'>('name');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<{ contact?: Contact | null; open: boolean }>({ open: false });

  const pick = useCallback((key: ListKey, tag?: string | null) => {
    const next = new URLSearchParams(params);
    next.set('list', key);
    if (tag) next.set('tag', tag); else next.delete('tag');
    setParams(next, { replace: true });
  }, [params, setParams]);

  const query = useMemo(() => ({
    scope: list === 'shared' ? 'SHARED' as const : list === 'starred' ? 'ALL' as const : 'PERSONAL' as const,
    starred: list === 'starred',
    tagId: tagId ?? undefined,
    q: q.trim() || undefined,
    sort,
  }), [list, tagId, q, sort]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await contactsApi.list({ ...query, limit: 200 });
      setRows(res.contacts);
      setTotal(res.total);
    } catch {
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [query]);

  /** 좌측 묶음의 개수 — 목록과 따로 센다(고른 것과 상관없이 늘 같은 수를 보여야 한다). */
  const reloadCounts = useCallback(async () => {
    try {
      const [starred, personal, shared] = await Promise.all([
        contactsApi.list({ scope: 'ALL', starred: true, limit: 1 }),
        contactsApi.list({ scope: 'PERSONAL', limit: 1 }),
        contactsApi.list({ scope: 'SHARED', limit: 1 }),
      ]);
      setCounts({ starred: starred.total, personal: personal.total, shared: shared.total });
    } catch { /* 개수를 못 세도 목록은 선다 */ }
  }, []);

  useEffect(() => { void reload(); }, [reload]);
  useEffect(() => { void reloadCounts(); }, [reloadCounts]);
  useEffect(() => { void contactsApi.listTags().then(setTags).catch(() => setTags([])); }, []);

  const afterChange = useCallback(() => {
    void reload();
    void reloadCounts();
  }, [reload, reloadCounts]);

  const toggleStar = useCallback(async (row: Contact) => {
    try {
      await contactsApi.update(row.id, { isStarred: !row.isStarred });
      afterChange();
    } catch { /* 실패하면 목록이 그대로다 — 다시 누르면 된다 */ }
  }, [afterChange]);

  const remove = useCallback(async (row: Contact) => {
    if (!window.confirm(tContacts('contacts.delete.confirm'))) return;
    try {
      await contactsApi.remove(row.id);
      afterChange();
    } catch { /* 지우지 못하면 줄이 남는다 */ }
  }, [afterChange]);

  /** 「메일 보내기」 — 메일 쓰기 화면으로 주소를 들고 간다. */
  const sendMail = useCallback((email: string) => {
    navigate(`/mail/compose?to=${encodeURIComponent(email)}`);
  }, [navigate]);

  const columns: ReadonlyArray<TableColumn<Contact>> = useMemo(() => [
    {
      key: 'star',
      header: '',
      width: '44px',
      align: 'center',
      render: (row) => (
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          aria-label={tContacts(row.isStarred ? 'contacts.row.star_on' : 'contacts.row.star_off')}
          aria-pressed={row.isStarred}
          className={row.isStarred ? 'ctp-star ctp-star--on' : 'ctp-star'}
          onClick={() => { void toggleStar(row); }}
        >
          <Star size={16} aria-hidden />
        </Button>
      ),
    },
    {
      key: 'name',
      header: tContacts('contacts.col.name'),
      rowHeader: true,
      render: (row) => {
        const name = row.name?.trim() ?? '';
        return (
          <span className="ctp-name">
            {/* 사진이 있으면 사진, 없으면 «종전 그대로» 이니셜 (2026-10-06 지시 3·4). */}
            <ContactPhoto photoFileId={row.photoFileId} name={row.name} />
            <span className={name ? '' : 'ctp-name__none'}>{name || tContacts('contacts.noname')}</span>
          </span>
        );
      },
    },
    { key: 'email', header: tContacts('contacts.col.email'), render: (row) => <span className="ctp-mono">{row.email}</span> },
    { key: 'phone', header: tContacts('contacts.col.phone'), render: (row) => row.phone ?? '—' },
    { key: 'mobile', header: tContacts('contacts.col.mobile'), render: (row) => row.mobile ?? '—' },
    { key: 'company', header: tContacts('contacts.col.company'), render: (row) => row.company ?? '—' },
    {
      key: 'tags',
      header: tContacts('contacts.col.tags'),
      render: (row) => (
        row.tags.length === 0 ? '—' : (
          <span className="ctp-tags">
            {row.tags.map((t) => <Badge key={t.id} variant="primary" size="sm">{t.name}</Badge>)}
          </span>
        )
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '116px',
      align: 'right',
      render: (row) => (
        <span className="ctp-rowact">
          <Button
            variant="secondary" size="sm" iconOnly
            aria-label={tContacts('contacts.row.mail')}
            title={tContacts('contacts.row.mail')}
            onClick={() => sendMail(row.email)}
          >
            <Mail size={14} aria-hidden />
          </Button>
          <Button
            variant="secondary" size="sm" iconOnly
            aria-label={tContacts('contacts.row.edit')}
            title={tContacts('contacts.row.edit')}
            disabled={row.scope === 'SHARED' && !canShare}
            onClick={() => setEditing({ contact: row, open: true })}
          >
            <Edit3 size={14} aria-hidden />
          </Button>
          {/* 줄 끝 삭제는 quiet-danger — 스무 줄이면 빨간 덩어리가 스무 개 선다(정본 D1). */}
          <Button
            variant="quiet-danger" size="sm" iconOnly
            aria-label={tContacts('contacts.row.delete')}
            title={tContacts('contacts.row.delete')}
            disabled={row.scope === 'SHARED' && !canShare}
            onClick={() => { void remove(row); }}
          >
            <Trash2 size={14} aria-hidden />
          </Button>
        </span>
      ),
    },
  ], [canShare, remove, sendMail, toggleStar]);

  const title = tContacts(
    list === 'shared' ? 'contacts.list.shared'
      : list === 'starred' ? 'contacts.list.starred'
        : 'contacts.list.personal',
  );
  const description = tContacts(
    list === 'shared' ? 'contacts.desc.shared'
      : list === 'starred' ? 'contacts.desc.starred'
        : 'contacts.desc.personal',
  );
  /* 공유 주소록에서도 「연락처 추가」는 모두에게 선다 (2026-09-15 · #1374 — 종전: 관리자만, 일반 사용자는 403 이었다). */
  const canAddHere = list !== 'shared' || canSaveShared;

  return (
    <div className="ctp">
      <ListPane
        title={tContacts('contacts.title')}
        ariaLabel={tContacts('contacts.side.aria')}
        className="ctp-side"
        action={canAddHere ? (
          <Button
            variant="primary"
            size="lg"
            fullWidth
            iconLeft={<Plus size={17} aria-hidden />}
            onClick={() => setEditing({ contact: null, open: true })}
          >
            {tContacts('contacts.add')}
          </Button>
        ) : undefined}
        footer={(
          /*
            ★ 정본 **A8 ⑤ 발** — 「하루에 몇 번 안 쓰는 것이 오는 조용한 자리」다.
              게시판의 「휴지통」이 그 규격(`ghost` · `sm`)이라 같은 무게로 맞춘다
              (2026-09-08 사용자 지적 — 처음엔 `secondary` + `fullWidth` 라 좌측 아래에
              회색 덩어리 하나가 서서, 조용한 자리가 아니라 **주 동작처럼** 보였다).
            ★ 새 화면을 만들지 않는다 — 이미 사는 자리(환경설정 「스팸」)로 보낸다.
          */
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<Ban size={14} aria-hidden />}
            onClick={() => navigate('/mail/settings?tab=spam')}
          >
            {tContacts('contacts.blocked.link')}
          </Button>
        )}
      >
        <ListPaneGroup label={tContacts('contacts.title')}>
          <ListPaneItem
            name={tContacts('contacts.list.starred')}
            icon={<Star size={16} aria-hidden />}
            badge={counts.starred}
            selected={list === 'starred' && !tagId}
            onClick={() => pick('starred')}
          />
          <ListPaneItem
            name={tContacts('contacts.list.personal')}
            icon={<User size={16} aria-hidden />}
            badge={counts.personal}
            selected={list === 'personal' && !tagId}
            onClick={() => pick('personal')}
          />
          <ListPaneItem
            name={tContacts('contacts.list.shared')}
            icon={<Users size={16} aria-hidden />}
            badge={counts.shared}
            selected={list === 'shared' && !tagId}
            onClick={() => pick('shared')}
          />
        </ListPaneGroup>
        {tags.length > 0 && (
          <ListPaneGroup label={tContacts('contacts.group.tags')}>
            {tags.map((t) => (
              <ListPaneItem
                key={t.id}
                name={t.name}
                icon={<Tag size={16} aria-hidden />}
                selected={tagId === t.id}
                onClick={() => pick(list, t.id)}
              />
            ))}
          </ListPaneGroup>
        )}
      </ListPane>

      <div className="ctp-main">
        <PageHeader
          title={title}
          breadcrumb={[tContacts('contacts.title'), title]}
          description={description}
        />

        {/*
          ★ 정본 **A4 Toolbar** 를 쓴다(2026-09-08 사용자 지적으로 갈아탐).
            처음엔 손으로 `div` 를 짰는데, 그러면 ① 안의 컨트롤이 32 로 서서 목록 위 줄이
            본문보다 두꺼워지고 ② 「찾기 · 필터」가 한 줄에 안 선다.
            `Toolbar` 는 높이 40 · **안의 컨트롤을 28 로 맞춘다** — 규격이 컴포넌트에 있다.
          ★ 왼쪽은 «무엇을 보는지»(찾기 · 태그 · 정렬), 오른쪽은 «무엇을 하는지» — 여기서는
            담기가 화면 머리(A3)에 이미 있어 오른쪽을 비운다.
        */}
        <Toolbar className="ctp-tools">
          <Input
            value={q}
            placeholder={tContacts('contacts.search.ph')}
            prefix={<Search size={15} aria-hidden />}
            aria-label={tContacts('contacts.search.ph')}
            wrapperClassName="ctp-tools__search"
            onChange={(e) => setQ(e.target.value)}
          />
          {/* ⚠ `null` 을 그대로 주면 빈 옵션('')과 안 맞아 placeholder(「선택」)가 뜬다. */}
          <Select
            value={tagId ?? ''}
            size="sm"
            ariaLabel={tContacts('contacts.group.tags')}
            onChange={(v) => pick(list, v === '' ? null : v)}
            options={[
              { value: '', label: tContacts('contacts.tag.all') },
              ...tags.map((t) => ({ value: t.id, label: t.name })),
            ]}
          />
          <Select
            value={sort}
            size="sm"
            ariaLabel={tContacts('contacts.sort.name')}
            onChange={(v) => setSort(v as 'name' | 'email' | 'recent')}
            options={[
              { value: 'name', label: tContacts('contacts.sort.name') },
              { value: 'email', label: tContacts('contacts.sort.email') },
              { value: 'recent', label: tContacts('contacts.sort.recent') },
            ]}
          />
        </Toolbar>

        {/*
          ★ 본문은 **떠 있는 면**(`--paper`) 위에 있다 — 정본 F2 「바닥은 --field, 뜨는 것은
            레일 · 메뉴 · 카드 · 드롭다운」. 표와 빈 화면을 바닥에 그대로 두면 «감싸는 것»이
            없어 다른 화면과 결이 달라진다(2026-09-08 사용자 지적).
          ★ `headless flush` — 제목은 화면 머리(A3)가 이미 말했고, 표는 카드 안쪽 여백 없이
            통째로 들어간다.
          ★ 빈 화면에는 「연락처 추가」를 두지 않는다 — 좌측 ②동작 자리에 **상시** 서 있다.
            정본 P3 의 「first-run + 만들기」는 그 자리가 없는 화면을 위한 규칙이다.
        */}
        <Card headless flush className="ctp-content">
          {!loading && rows.length === 0 ? (
            <EmptyState
              icon={ContactIcon}
              title={tContacts(q.trim() ? 'contacts.empty.search' : 'contacts.empty.title')}
              description={q.trim() ? undefined : tContacts('contacts.empty.desc')}
            />
          ) : (
            <Table
              caption={`${title} · ${total}`}
              columns={columns}
              rows={rows}
              rowKey={(row) => row.id}
              loading={loading}
            />
          )}
        </Card>
      </div>

      {editing.open && (
        <ContactEditModal
          contact={editing.contact}
          canShare={canSaveShared}
          defaultScope={list === 'shared' ? 'SHARED' : 'PERSONAL'}
          tags={tags}
          onClose={() => setEditing({ open: false })}
          onSaved={() => {
            setEditing({ open: false });
            afterChange();
            void contactsApi.listTags().then(setTags).catch(() => undefined);
          }}
        />
      )}
    </div>
  );
}
